import React, { useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "./DashboardV3.css";

import { useAuth } from "./../context/AuthContext";
import { LOGOUT } from "../constants/Constants";
import DataSevices from "../Services/DataSevices";
import { Link, useNavigate } from "react-router-dom";

/* =========================================================================
   CONSTANTS
   ========================================================================= */

const TARGET_ROUNDS = 16; // daily chanting-round goal used for the bead ring
const WHATSAPP_CC = "91"; // country code used when building wa.me links

const championSlides = [
  { id: 1, image: "/july2026winners.png" },
  { id: 2, image: "/june26winners.png" },
  { id: 3, image: "/may26winners.png" },
];

/* =========================================================================
   SMALL HELPERS
   ========================================================================= */

const firstName = (name = "") => name.trim().split(" ")[0];

const getLast7Dates = () => {
  const dates = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - i);
    dates.push(d);
  }
  return dates;
};

const getDateKey = (date) => {
  const d = new Date(date);

  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

const isSameDay = (a, b) => {
  return getDateKey(a) === getDateKey(b);
};


//console.log("mockChantingFor examples:", mockChantingFor)
const daysUntilNextBirthday = (dobStr) => {
  if (!dobStr) return null;
  const dob = new Date(dobStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let next = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());
  if (next < today) next.setFullYear(today.getFullYear() + 1);
  return Math.round((next - today) / 86400000);
};

const birthdayLabel = (daysUntil, dobStr) => {
  if (!dobStr) return "";
  const dob = new Date(dobStr);
  if (daysUntil === 0) return "Today";
  if (daysUntil === 1) return "Tomorrow";

  return dob.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
  });
};

const birthdayBucket = (daysUntil) => {
  if (daysUntil <= 0) return "today";
  if (daysUntil <= 6) return "thisWeek";
  return "nextWeek";
};

/* ---- WhatsApp message builders (placeholders - no API wired up) -------- */

const buildWhatsAppMessage = (type, devotee, ctx = {}) => {
  const name = firstName(devotee?.name);
  const { attendance = [], chanting = [] } = ctx;

  switch (type) {
    case "birthday":
      return `Hare Krishna ${name} 🎉🪔

Wishing you a very happy birthday.
May Krishna bless you with good health, devotion and ever-increasing taste for chanting His holy names. 🙏

Hare Krishna Hare Krishna Krishna Krishna Hare Hare
Hare Rama Hare Rama Rama Rama Hare Hare`;

    case "attendanceReminder":
      return `Hare Krishna ${name} 🙏

We noticed you haven't joined today's Zoom Japa session yet. We'd love to have you with us — please try to join!

GVS Zoom Japa Attendance
https://zoom.gaurangavedic.org.in`;

    case "chantingReminder":
      return `Hare Krishna ${name} 🙏

Just a gentle reminder to log today's chanting rounds. Every round counts — keep the momentum going! 📿

GVS Zoom Japa Attendance
https://zoom.gaurangavedic.org.in`;

    case "attendanceReport": {
      const lines = attendance
        .map((a) => `${a.dateLabel} (${a.dayLabel}) - ${a.present ? "Present" : "Absent"}`)
        .join("\n");
      const presentDays = attendance.filter((a) => a.present).length;
      return `Hare Krishna ${name}

Your last 7 days Zoom Japa attendance -
${lines}

Summary: ${presentDays}/7 days

GVS Zoom Japa Attendance
https://zoom.gaurangavedic.org.in`;
    }

        case "chantingSummary": {
          const lines = chanting
            .map((c) => `${c.dateLabel} (${c.dayLabel}) - ${c.submitted ? `${c.rounds} rounds` : "No update"}`)
            .join("\n");
           const totalRounds = chanting.reduce(
              (sum, c) => sum + Number(c.rounds || 0),
              0
            );
          return `Hare Krishna ${name}

Your last 7 days chanting rounds -
${lines}

Total: ${totalRounds} rounds this week 📿

GVS Zoom Japa Attendance
https://zoom.gaurangavedic.org.in`;
    }

    case "greeting":
    default:
      return `Hare Krishna ${name} 🙏

Hare Krishna Hare Krishna Krishna Krishna Hare Hare
Hare Rama Hare Rama Rama Rama Hare Hare`;
  }
};

const openWhatsApp = (devotee, type, ctx) => {
  if (!devotee?.whatsApp) {
    alert(`WhatsApp number is not available for ${devotee?.name}`);
    return;
  }
  const phone = WHATSAPP_CC + String(devotee.whatsApp).replace(/\D/g, "");
  const message = buildWhatsAppMessage(type, devotee, ctx);
  window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, "_blank");
};

/* =========================================================================
   DUMMY DATA
   ========================================================================= */

const DUMMY_CHANTING_ROUNDS = {
  "go@gmail.com": [
    { date: "2026-08-14", rounds: 16, submitted: true },
    { date: "2026-08-15", rounds: 16, submitted: true },
    { date: "2026-08-16", rounds: 12, submitted: true },
    { date: "2026-08-17", rounds: 16, submitted: true },
    { date: "2026-08-18", rounds: 16, submitted: true },
    { date: "2026-08-19", rounds: 14, submitted: true },
    { date: "2026-08-20", rounds: 16, submitted: true },
  ],

  "devotee2@example.com": [
    { date: "2026-08-14", rounds: 8, submitted: true },
    { date: "2026-08-15", rounds: 12, submitted: true },
    { date: "2026-08-16", rounds: 0, submitted: false },
    { date: "2026-08-17", rounds: 16, submitted: true },
    { date: "2026-08-18", rounds: 10, submitted: true },
    { date: "2026-08-19", rounds: 12, submitted: true },
    { date: "2026-08-20", rounds: 8, submitted: true },
  ],

  "devotee3@example.com": [
    { date: "2026-08-14", rounds: 16, submitted: true },
    { date: "2026-08-15", rounds: 16, submitted: true },
    { date: "2026-08-16", rounds: 16, submitted: true },
    { date: "2026-08-17", rounds: 16, submitted: true },
    { date: "2026-08-18", rounds: 0, submitted: false },
    { date: "2026-08-19", rounds: 16, submitted: true },
    { date: "2026-08-20", rounds: 16, submitted: true },
  ],

  "devotee4@example.com": [
    { date: "2026-08-14", rounds: 4, submitted: true },
    { date: "2026-08-15", rounds: 8, submitted: true },
    { date: "2026-08-16", rounds: 6, submitted: true },
    { date: "2026-08-17", rounds: 0, submitted: false },
    { date: "2026-08-18", rounds: 10, submitted: true },
    { date: "2026-08-19", rounds: 8, submitted: true },
    { date: "2026-08-20", rounds: 6, submitted: true },
  ],

  "devotee5@example.com": [
    { date: "2026-08-14", rounds: 16, submitted: true },
    { date: "2026-08-15", rounds: 14, submitted: true },
    { date: "2026-08-16", rounds: 16, submitted: true },
    { date: "2026-08-17", rounds: 12, submitted: true },
    { date: "2026-08-18", rounds: 16, submitted: true },
    { date: "2026-08-19", rounds: 16, submitted: true },
    { date: "2026-08-20", rounds: 16, submitted: true },
  ],
};

const mockChantingFor = (email) => {
  return (DUMMY_CHANTING_ROUNDS[email] || []).map((item) => ({
    ...item,
    date: new Date(item.date),
  }));
};

/* =========================================================================
   COMPONENT
   ========================================================================= */

const DashboardV3 = () => {
  const [data, setData] = useState([]);
  const [facilitiesAttendance7Days, setFacilitiesAttendance7Days] = useState({});
  const [devoteesDOB, setDevoteesDOB] = useState([]);
  const [chantingRounds7Days, setChantingRounds7Days] = useState({});
  const [top3CurrentMonth, setTop3CurrentMonth] = useState([]);
  const [searchquery, setSearchquery] = useState("");
  const [activeTab, setActiveTab] = useState("attendance"); // 'attendance' | 'chanting'
  const [activeFilter, setActiveFilter] = useState("all");
  const navigate = useNavigate();
  const { logout } = useAuth();

  const today = new Date();
  const formattedDate = today.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  useEffect(() => {
    if (sessionStorage.getItem("userEmail") == null) navigate("/");
  }, []);

  useEffect(() => {
    DataSevices.getDevoteesDataforDOB()
        .then((res) => {
            setDevoteesDOB(res.data)
        })
  }, [])

  // Devotees + last-7-days attendance (existing functionality, unchanged)
  useEffect(() => {
    DataSevices.getFacilitiesDataByEmail(sessionStorage.getItem("userEmail"))
      .then((res) => {
        setData(res.data);
        const emails = res.data.map((r) => r.email).join(",");
        DataSevices.last7DaysAttendance(emails)
          .then((attendanceRes) => setFacilitiesAttendance7Days(attendanceRes.data))
          .catch((err) => console.log("Error fetching attendance data:", err));
      })
      .catch((err) => console.log(err));
  }, []);

    useEffect(() => {
      DataSevices.getFacilitiesDataByEmail(sessionStorage.getItem("userEmail"))
        .then((res) => {
          setData(res.data);
          const emails = res.data.map((r) => r.email).join(",");
          DataSevices.last7DaysChantingRounds(emails)
            .then((chantingRes) => setChantingRounds7Days(chantingRes.data))
            .catch((err) => console.log("Error fetching chanting record data:", err));
        })
        .catch((err) => console.log(err));
    }, []);

  // Scoreboard (existing functionality, unchanged)
  useEffect(() => {
    DataSevices.top3participantsByDaysInCurrentMonth()
      .then((res) => setTop3CurrentMonth(res.data))
      .catch((err) => console.log(err));
  }, []);


  const handleLogout = async () => {
    sessionStorage.clear();
    logout();
    await fetch(LOGOUT, { method: "POST", credentials: "include" }).then(() => navigate("/"));
  };

  const groupByFacGroup = (list) =>
    list.reduce((acc, curr) => {
      const group = curr.fac_group || "UNKNOWN";
      if (!acc[group]) acc[group] = [];
      acc[group].push(curr);
      return acc;
    }, {});
  const groupedCurrent = groupByFacGroup(top3CurrentMonth);

  /* ---------------- Derived: birthdays ---------------- */
const birthdays = useMemo(() => {
  return devoteesDOB
    .filter((d) => d.dob) // Ignore devotees without DOB
    .map((d) => {
      const dob = d.dob;
      const daysUntil = daysUntilNextBirthday(dob);

      return {
        ...d,
        dob,
        daysUntil,
        bucket: birthdayBucket(daysUntil),
      };
    })
    .filter((d) => d.daysUntil <= 13)
    .sort((a, b) => a.daysUntil - b.daysUntil);
}, [devoteesDOB]);

  const todayBirthdays = birthdays.filter((b) => b.bucket === "today");
  const weekBirthdays = birthdays.filter((b) => b.bucket === "thisWeek");
  const nextWeekBirthdays = birthdays.filter((b) => b.bucket === "nextWeek");
  const todayBirthdayEmails = new Set(todayBirthdays.map((b) => b.email));

  /* ---------------- Derived: per-devotee day grids ---------------- */
  const buildAttendanceCtx = (email) => {
    const raw = facilitiesAttendance7Days?.[email] || [];
    return raw
      .slice()
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((item) => ({
        dateLabel: new Date(item.date).getDate(),
        dayLabel: new Date(item.date).toLocaleDateString("en-US", { weekday: "short" }),
        present: item.status === "PRESENT",
        joinTime: item.joinTime,
        rawDate: item.date,
      }));
  };

  const buildChantingCtx = (email) => {
    const raw = chantingRounds7Days?.[email] || [];
    return raw
      .slice()
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((item) => ({
        dateLabel: new Date(item.date).getDate(),
        dayLabel: new Date(item.date).toLocaleDateString("en-US", { weekday: "short" }),
        rounds: item.rounds || 0,
        submitted: true,
        rawDate: item.date,
      }));
  };

  const isAttendanceMissingToday = (email) => {
    const arr = facilitiesAttendance7Days?.[email] || [];
    const entry = arr.find((a) => isSameDay(a.date, today));
    return !entry || entry.status !== "PRESENT";
  };

    const isChantingMissingToday = (email) => {
      const arr = chantingRounds7Days?.[email] || [];
      const entry = arr.find((c) => isSameDay(c.date, today));

      return !entry || Number(entry.rounds) === 0;
    };

  const chantingConsistency = (email) => {
    const arr = chantingRounds7Days?.[email] || [];
    const good = arr.filter((c) => c.submitted && c.rounds > 0).length;
    return { good, total: arr.length || 7 };
  };

  /* ---------------- Derived: filtered devotee list ---------------- */
  const filteredData = useMemo(() => {
    let list = data.filter((res) => res.name.toLowerCase().includes(searchquery.toLowerCase()));
    if (activeFilter === "missingAttendance") list = list.filter((r) => isAttendanceMissingToday(r.email));
    if (activeFilter === "missingChanting") list = list.filter((r) => isChantingMissingToday(r.email));
    return list;
  }, [data, searchquery, activeFilter, facilitiesAttendance7Days, chantingRounds7Days, todayBirthdayEmails]);

  const missingAttendanceCount = data.filter((r) => isAttendanceMissingToday(r.email)).length;
  const missingChantingCount = data.filter((r) => isChantingMissingToday(r.email)).length;

  /* ========================================================================= */

  return (
    <div className="dv3-page">
      {/* HEADER */}
      <div className="dv3-header">
        <div className="container-fluid">
          <div className="row align-items-center gy-2">
            <div className="col-lg-3 col-md-12">
              <div className="d-flex align-items-center">
                <div className="dv3-logo-circle me-3">
                  <img src="/GVS.ico" alt="logo" className="dv3-logo-img" />
                </div>
                <h6 className="mb-0 text-white fw-bold">GVS Zoom Japa Attendance</h6>
              </div>
            </div>

            <div className="col-lg-5 text-center">
              <div className="dv3-mantra-box">
                Hare Krishna Hare Krishna Krishna Krishna Hare Hare
                <br />
                Hare Rama Hare Rama Rama Rama Hare Hare
              </div>
            </div>

            <div className="col-lg-4">
              <div className="d-flex justify-content-lg-end justify-content-center align-items-center gap-3">
                <div className="dv3-date-box">
                  <div className="dv3-date-label">Today is</div>
                  <strong>{formattedDate}</strong>
                </div>
                <button className="btn btn-outline-light dv3-logout-btn" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid dv3-body">
        {/* STATS STRIP */}
        <div className="row g-3 dv3-stats-row">

          {/* TOTAL DEVOTEES */}
          <div className="col-6 col-lg">
            <div className="dv3-stat-card">
              <div className="dv3-stat-icon purple">
                <i className="bi bi-people-fill"></i>
              </div>
              <div>
                <h6>Total Devotees</h6>
                <h2>{devoteesDOB.length}</h2>
                <span className="text-success">Active</span>
              </div>
            </div>
          </div>

          {/* ALUMNI */}
          <div className="col-6 col-lg">
            <div className="dv3-stat-card">
              <div className="dv3-stat-icon blue">
                <i className="bi bi-check-circle-fill"></i>
              </div>
              <div>
                <h6>ALUMNI</h6>
                <h2>
                  {
                    devoteesDOB.filter(
                      (a) =>
                        a.group === "ALUMNI" &&
                        a.zoomStatus === "VERIFIED"
                    ).length
                  }
                  /
                  {
                    devoteesDOB.filter(
                      (a) => a.group === "ALUMNI"
                    ).length
                  }
                </h2>
                <span className="text-success">Verified</span>
              </div>
            </div>
          </div>

          {/* GAURANGI */}
          <div className="col-6 col-lg">
            <div className="dv3-stat-card">
              <div className="dv3-stat-icon blue">
                <i className="bi bi-check-circle-fill"></i>
              </div>
              <div>
                <h6>GAURANGI</h6>
                <h2>
                  {
                    devoteesDOB.filter(
                      (a) =>
                        a.group === "GAURANGI" &&
                        a.zoomStatus === "VERIFIED"
                    ).length
                  }
                  /
                  {
                    devoteesDOB.filter(
                      (a) => a.group === "GAURANGI"
                    ).length
                  }
                </h2>
                <span className="text-success">Verified</span>
              </div>
            </div>
          </div>

          {/* YASHODA */}
          <div className="col-6 col-lg">
            <div className="dv3-stat-card">
              <div className="dv3-stat-icon blue">
                <i className="bi bi-check-circle-fill"></i>
              </div>
              <div>
                <h6>YASHODA</h6>
                <h2>
                  {
                    devoteesDOB.filter(
                      (a) =>
                        a.group === "YASHODA" &&
                        a.zoomStatus === "VERIFIED"
                    ).length
                  }
                  /
                  {
                    devoteesDOB.filter(
                      (a) => a.group === "YASHODA"
                    ).length
                  }
                </h2>
                <span className="text-success">Verified</span>
              </div>
            </div>
          </div>

          {/* NANDBABA */}
          <div className="col-6 col-lg">
            <div className="dv3-stat-card">
              <div className="dv3-stat-icon rose">
                <i className="bi bi-check-circle-fill"></i>
              </div>
              <div>
                <h6>NANDBABA</h6>
                <h2>
                  {
                    devoteesDOB.filter(
                      (a) =>
                        a.group === "NANDBABA" &&
                        a.zoomStatus === "VERIFIED"
                    ).length
                  }
                  /
                  {
                    devoteesDOB.filter(
                      (a) => a.group === "NANDBABA"
                    ).length
                  }
                </h2>
                <span className="text-success">Verified</span>
              </div>
            </div>
          </div>

        </div>

        {/* BIRTHDAY RAIL */}
        <div className="dv3-birthday-card">
          <div className="dv3-birthday-header">
            <h6>
              <i className="bi bi-gift-fill"></i> Birthdays
            </h6>
            <div className="dv3-birthday-counts">
              <span className="dv3-count-chip today">{todayBirthdays.length} Today</span>
              <span className="dv3-count-chip week">{weekBirthdays.length} This Week</span>
              <span className="dv3-count-chip next">{nextWeekBirthdays.length} Next Week</span>
            </div>
          </div>

          <div className="dv3-birthday-scroll">
            {birthdays.length === 0 && (
              <div className="dv3-birthday-empty">No birthdays in the next two weeks 🌸</div>
            )}

            {birthdays.map((b) => (
              <div key={b.email} className={`dv3-birthday-chip ${b.bucket === "today" ? "is-today" : ""}`}>
                <div className="dv3-birthday-avatar">
                  {b.bucket === "today" ? <i className="bi bi-stars"></i> : <i className="bi bi-person-fill"></i>}
                </div>
                <div className="dv3-birthday-info">
                  <div className="dv3-birthday-name">{b.name}</div>
                  <div className="dv3-birthday-when">
                    {birthdayLabel(b.daysUntil, b.dob)}
                  </div>
                </div>
                {b.bucket === "today" && (
                  <button
                    className="dv3-birthday-wish-btn"
                    title="Send birthday wishes on WhatsApp"
                    onClick={() => openWhatsApp(b, "birthday")}
                  >
                    <i className="bi bi-whatsapp"></i>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className="row g-4 dv3-main-row">
          {/* LEFT: DEVOTEE LIST */}
          <div className="col-lg-8">
            <div className="dv3-card dv3-list-card">
              <div className="dv3-list-toolbar">
                <ul className="nav nav-pills dv3-tabs">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === "attendance" ? "active" : ""}`}
                      onClick={() => {
                        setActiveTab("attendance");
                        setActiveFilter("all");
                      }}
                    >
                      <i className="bi bi-camera-video"></i> Attendance
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === "chanting" ? "active" : ""}`}
                      onClick={() => {
                        setActiveTab("chanting");
                        setActiveFilter("all");
                      }}
                    >
                      <i className="bi bi-flower1"></i> Chanting Rounds
                    </button>
                  </li>
                </ul>

                <input
                  className="form-control dv3-search-box"
                  placeholder="Search devotee..."
                  value={searchquery}
                  onChange={(e) => setSearchquery(e.target.value)}
                />
              </div>

              <div className="dv3-filter-chips">
                {/* Always available */}
                <button
                  className={`dv3-chip ${activeFilter === "all" ? "active" : ""}`}
                  onClick={() => setActiveFilter("all")}
                >
                  All ({data.length})
                </button>



                {/* Attendance-specific filter */}
                {activeTab === "attendance" && (
                  <button
                    className={`dv3-chip warn ${
                      activeFilter === "missingAttendance" ? "active" : ""
                    }`}
                    onClick={() => setActiveFilter("missingAttendance")}
                  >
                    Missing Attendance ({missingAttendanceCount})
                  </button>
                )}

                {/* Chanting-specific filter */}
                {activeTab === "chanting" && (
                  <button
                    className={`dv3-chip warn ${
                      activeFilter === "missingChanting" ? "active" : ""
                    }`}
                    onClick={() => setActiveFilter("missingChanting")}
                  >
                    Missing Chanting ({missingChantingCount})
                  </button>
                )}
              </div>

              <div className="dv3-devotee-list">
                {filteredData.length > 0 ? (
                  filteredData.map((res, idx) => {
                    const attendance = buildAttendanceCtx(res.email);
                    const chanting = buildChantingCtx(res.email);
                    const consistency = chantingConsistency(res.email);
                    const isBirthdayToday = todayBirthdayEmails.has(res.email);
                    const missingAttToday = isAttendanceMissingToday(res.email);
                    const missingChantToday = isChantingMissingToday(res.email);
                    const ctx = { attendance, chanting };

                    let consistencyClass = "low";
                    if (consistency.good >= 6) consistencyClass = "good";
                    else if (consistency.good >= 3) consistencyClass = "mid";

                    return (
                      <div key={res.email || idx} className={`dv3-row ${isBirthdayToday ? "is-birthday" : ""}`}>
                        <div className="dv3-devotee-info">
                          <div className="dv3-avatar">
                            <i className="bi bi-person-fill"></i>
                            {isBirthdayToday && <span className="dv3-avatar-badge">🎂</span>}
                          </div>

                          <div className="dv3-devotee-text">
                            <div className="dv3-devotee-name">
                              {res.name}
                              <span className={`badge dv3-badge ${res.zoomStatus === "VERIFIED" ? "bg-success" : "bg-danger"}`}>
                                {res.zoomStatus}
                              </span>
                            </div>
                             <div className="bg-light small">
                                {res.email}
                            </div>
                          </div>
                        </div>

                        <div className="dv3-days-wrapper">
                          {activeTab === "attendance"
                            ? attendance.map((a, i) => (
                                <div
                                  key={i}
                                  className={a.present ? "dv3-day-pill present" : "dv3-day-pill absent"}
                                  data-bs-toggle="tooltip"
                                  data-bs-html="true"
                                  title={`Date: ${a.rawDate} Time: ${a.joinTime || "-"}`}
                                >
                                  <div>{a.dateLabel}</div>
                                  <small>{a.dayLabel}</small>
                                </div>
                              ))
                            : chanting.map((c, i) => {
                                const pct = Math.min(100, Math.round((c.rounds / TARGET_ROUNDS) * 100));
                                let ringColor = "#B9B4AC";
                                let stateClass = "no-update";
                                if (!c.submitted) {
                                  ringColor = "#B9B4AC";
                                  stateClass = "no-update";
                                } else if (c.rounds === 0) {
                                  ringColor = "#D64545";
                                  stateClass = "missed";
                                } else if (c.rounds < TARGET_ROUNDS) {
                                  ringColor = "#E8A33D";
                                  stateClass = "partial";
                                } else {
                                  ringColor = "#1F9D55";
                                  stateClass = "complete";
                                }
                                return (
                                  <div
                                    key={i}
                                    className={`dv3-round-cell ${stateClass}`}
                                    data-bs-toggle="tooltip"
                                    title={c.submitted ? `${c.rounds} rounds` : "No update submitted"}
                                  >
                                    <div className="dv3-round-ring" style={{ "--pct": `${pct}%`, "--ring-color": ringColor }}>
                                      <span>{c.submitted ? c.rounds : "–"}</span>
                                    </div>
                                    <small>{c.dayLabel}</small>
                                  </div>
                                );
                              })}
                        </div>

                        <div className="dv3-actions">
                          <div className="dropdown">
                            <button
                              className="dv3-whatsapp-btn dropdown-toggle"
                              type="button"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                              title="WhatsApp actions"
                            >
                              <i className="bi bi-whatsapp"></i>
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end shadow-sm dv3-dropdown">
                              {isBirthdayToday && (
                                <li>
                                  <button className="dropdown-item dv3-item-birthday" onClick={() => openWhatsApp(res, "birthday", ctx)}>
                                    <i className="bi bi-gift-fill"></i> Send Birthday Wishes
                                  </button>
                                </li>
                              )}
                              {missingAttToday && (
                                <li>
                                  <button className="dropdown-item" onClick={() => openWhatsApp(res, "attendanceReminder", ctx)}>
                                    <i className="bi bi-camera-video"></i> Remind: Today's Attendance
                                  </button>
                                </li>
                              )}
                              {missingChantToday && (
                                <li>
                                  <button className="dropdown-item" onClick={() => openWhatsApp(res, "chantingReminder", ctx)}>
                                    <i className="bi bi-flower1"></i> Remind: Today's Chanting
                                  </button>
                                </li>
                              )}
                              {(isBirthdayToday || missingAttToday || missingChantToday) && (
                                <li>
                                  <hr className="dropdown-divider" />
                                </li>
                              )}
                              <li>
                                <button className="dropdown-item" onClick={() => openWhatsApp(res, "attendanceReport", ctx)}>
                                  <i className="bi bi-bar-chart-fill"></i> Weekly Attendance Report
                                </button>
                              </li>
                              <li>
                                <button className="dropdown-item" onClick={() => openWhatsApp(res, "chantingSummary", ctx)}>
                                  <i className="bi bi-journal-text"></i> Weekly Chanting Summary
                                </button>
                              </li>
                              <li>
                                <button className="dropdown-item" onClick={() => openWhatsApp(res, "greeting", ctx)}>
                                  <i className="bi bi-chat-dots-fill"></i> Say Hare Krishna 🙏
                                </button>
                              </li>
                            </ul>
                          </div>

                          <Link
                            to="devoteesJapa"
                            state={{ email: res?.email, name: res?.name }}
                            className="dv3-view-btn text-decoration-none"
                          >
                            <i className="bi bi-eye"></i>
                            <span className="ms-1">View</span>
                          </Link>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center p-5 dv3-empty">
                    <div className="fs-2">🔎</div>
                    <div>No devotees found</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: SPOTLIGHT + SCOREBOARD */}
          <div className="col-lg-4">
            <div className="dv3-card dv3-spotlight-card">
              <div
                id="dv3SpotlightCarousel"
                className="carousel slide"
                data-bs-ride="carousel"
                data-bs-interval="3500"
              >
                <div className="carousel-inner">
                  {championSlides.map((slide, index) => (
                    <div key={slide.id} className={`carousel-item ${index === 0 ? "active" : ""}`}>
                      <img src={slide.image} className="dv3-spotlight-img" alt={`Champion ${slide.id}`} />
                    </div>
                  ))}
                </div>
                <div className="carousel-indicators dv3-spotlight-indicators">
                  {championSlides.map((slide, index) => (
                    <button
                      key={slide.id}
                      type="button"
                      data-bs-target="#dv3SpotlightCarousel"
                      data-bs-slide-to={index}
                      className={index === 0 ? "active" : ""}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="dv3-card dv3-scoreboard-card">
              <h6 className="dv3-scoreboard-title">
                <i className="bi bi-trophy-fill"></i> Scoreboard
              </h6>

              <table className="table table-sm align-middle dv3-score-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Days</th>
                    <th>Avg</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(groupedCurrent).map((group) => (
                    <React.Fragment key={group}>
                      <tr>
                        <td colSpan="4" className="dv3-group-row">
                          {group}
                        </td>
                      </tr>
                      {groupedCurrent[group]
                        .sort((a, b) => b.days_count - a.days_count)
                        .slice(0, 3)
                        .map((p, index) => (
                          <tr key={p.participant_email}>
                            <td>{index + 1}</td>
                            <td className="dv3-score-name" title={p.devotee_name}>
                              {p.devotee_name}
                            </td>
                            <td>{p.days_count}</td>
                            <td>{p.avg_per_day}</td>
                          </tr>
                        ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>

              <div className="dv3-scoreboard-footer">🌼 Keep chanting, keep growing spiritually! 🙏</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardV3;